import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import cn from 'clsx';
import { pick } from 'lodash';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import { GET_DATA_REVIEW_TABLES, GET_PANELS, SAVE_DATA_REVIEW_TABLE } from 'queries/central-monitoring';
import { DataReviewType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { formatColumn } from 'utils/data-table.service';
import NotificationService from 'utils/notification';
import AddColumn from 'views/components/admin/studies/data-review-tables/add-column';
import { DataReviewColumnConst } from 'views/components/admin/studies/data-review-tables/column-pick-const';
import DetailsExpandable from 'views/components/admin/studies/data-review-tables/details-expandable';
import EditColumnPanel from 'views/components/admin/studies/data-review-tables/edit-column-panel';
import { PreviewTable, LegacyPreviewTable } from 'views/components/admin/studies/data-review-tables/preview-table';
import { CustomDialog, DialogContent } from 'views/components/core/dialog';

import './edit-columns.scss';
import {
    getPanels,
    getPanelsVariables,
    saveDataReviewTable,
    saveDataReviewTableVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    StudyRestrictedFields,
    DataReviewTableFields,
    DataReviewTableFields_DataReviewTable_columns_DataReviewTableColumn as DataReviewTableFields_columns,
} from '@queries/generated/fragments.generated';

interface IEditColumns {
    onClose: () => void;
    currentTable: DataReviewTableFields;
    study: StudyRestrictedFields | null | undefined;
    canEdit: boolean;
}

export interface DataReviewTableColumns extends DataReviewTableFields_columns {
    index: number;
    id: number;
    _dataField: string | null;
    _type: string | null;
    _numeratorField: string | null;
    _denominatorField: string | null;
    _dataSourceName: string | null;
    _dataString: string;
    _panelId: string | null;
    _panelName: string | null;
    _panelField: string | null;
}

const EditColumns = ({ onClose, currentTable, study, canEdit }: IEditColumns) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();
    const [saveTable] = useMutation<saveDataReviewTable, saveDataReviewTableVariables>(SAVE_DATA_REVIEW_TABLE);
    const [active, setActive] = useState(0);
    const [currentColumn, setCurrentColumn] = useState<DataReviewTableColumns | null>(null);
    const [columns, setColumns] = useState<DataReviewTableColumns[]>([]);
    const addColumnOpen = useBoolean(false);
    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const showWarningModal = useBoolean(false);
    const changesWereMade = useBoolean(false);

    const { data: panelsData } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: localState?.selectedOrganisation?.id,
            studyId: localState?.selectedStudy?.id,
        },
    });

    useEffect(() => {
        if (currentTable?.columns && localState?.fields)
            setColumns(
                currentTable.columns.map((item, index) => ({
                    ...formatColumn(localState.fields, localState?.dataSources, item, index, panelsData?.panels || []),
                })),
            );
    }, [currentTable, localState, panelsData?.panels]);

    const close = () => {
        setActive(0);
        if (changesWereMade.value) showWarningModal.setTrue();
        else onClose();
    };

    useEffect(() => {
        const columnsToCompare = currentTable?.columns?.map((item, index) => ({
            ...formatColumn(localState?.fields, localState?.dataSources, item, index, panelsData?.panels || []),
        }));
        disabledSave.setValue(JSON.stringify(columns) === JSON.stringify(columnsToCompare));
    }, [columns, panelsData?.panels]);

    const saveColumn = () => {
        if (!canEdit || disabledSave.value) return;

        saving.setTrue();
        const columnsArray = columns.map((item) => {
            const col = pick(item, DataReviewColumnConst);
            if (!col.columnId) col.columnId = Constants.ObjectId.Empty;

            return col;
        });
        saveTable({
            variables: {
                table: {
                    active: currentTable.active,
                    description: currentTable.description,
                    id: currentTable.id,
                    name: currentTable.name,
                    studyId: currentTable.studyId,
                    dataSource:
                        currentTable.type === DataReviewType.COUNTRY || currentTable.type === DataReviewType.SITE
                            ? 'sitesAll'
                            : currentTable.type === DataReviewType.SUBJECT
                              ? 'subjectsAll'
                              : currentTable.dataSource,
                    type: currentTable.type,
                    columns: columnsArray,
                },
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_DATA_REVIEW_TABLES,
                    variables: {
                        studyId: study?.id,
                    },
                },
            ],
        })
            .then(() => {
                saving.setFalse();
                changesWereMade.setFalse();
                disabledSave.setTrue();
                if (currentTable.columns && currentTable.columns.length > columns.length)
                    NotificationService.showNotification(t('admin:columnSuccessfulDeleted'));
                else if (currentTable.columns && currentTable.columns.length < columns.length)
                    NotificationService.showNotification(t('admin:columnSuccessfulAdded'));
                else NotificationService.showNotification(t('admin:columnSuccessfulUpdated'));
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log('error', error);
                NotificationService.showNotification(t('errorMessage'));
                saving.setFalse();
            });
    };

    return (
        <>
            <WarningLostDataDialog
                open={showWarningModal.value}
                onClose={(action: string) => {
                    if (action === 'close') {
                        changesWereMade.setFalse();
                        onClose();
                    }
                    showWarningModal.setFalse();
                }}
            />
            <AddColumn
                currentColumn={currentColumn}
                open={addColumnOpen.value}
                onClose={(column) => {
                    if (column) {
                        const array = columns.map((col, idx) => ({ ...col, index: idx }));
                        const colIndex = array.findIndex((item) => item.id === column.id);
                        const index = currentColumn ? colIndex : array.length;
                        const value = formatColumn(
                            localState?.fields,
                            localState?.dataSources,
                            column,
                            index,
                            panelsData?.panels || [],
                        );
                        if (currentColumn) array[value.index] = value;
                        else array.push(value);
                        setColumns(array);
                        changesWereMade.setTrue();
                    }
                    setCurrentColumn(null);
                    addColumnOpen.setFalse();
                }}
                currentTable={currentTable}
            />
            <CustomDialog className="full-screen-dialog" onClose={close} fullWidth scroll="paper" open fullScreen>
                <div className="details-sticky-top">
                    <div className="details-expandable">
                        <DetailsExpandable
                            label={t('admin:tableDialogLabel', { id: currentTable?.recordId })}
                            table={currentTable}
                            onClose={close}
                        />
                    </div>
                    <div className="tab-menu-wrapper">
                        <Tabs value={active} className="details-tabs" onChange={(_, value) => setActive(value)}>
                            <Tab label={t('admin:editColumns')} data-testid="edit-columns-tab" />
                            {!!columns.length && (
                                <Tab label={t('admin:previewTable')} data-testid="preview-table-tab" />
                            )}
                        </Tabs>
                        <div className="menu">
                            {saving.value && <p className="saving-status">{t('saving')}</p>}
                            <div
                                role="button"
                                tabIndex={0}
                                className={cn('table-modal-custom-save-btn', {
                                    disabled: !canEdit || disabledSave.value,
                                })}
                                onKeyDown={saveColumn}
                                onClick={saveColumn}
                                data-testid="table-modal-custom-save-btn"
                            >
                                {t('save')}
                            </div>
                        </div>
                    </div>
                </div>
                <DialogContent className="risk-details-content" style={{ paddingTop: 20 }}>
                    {active === 0 ? (
                        <EditColumnPanel
                            panelItems={columns}
                            disabled={!canEdit}
                            onOpenColumnDialog={(item) => {
                                if (item) setCurrentColumn(item);
                                addColumnOpen.setTrue();
                            }}
                            setPanelItems={(items) => {
                                changesWereMade.setTrue();
                                disabledSave.setFalse();
                                setColumns(items);
                            }}
                        />
                    ) : localState?.selectedStudy?.useLatestCentralMonitoring ? (
                        <PreviewTable
                            table={{ ...currentTable, columns, study }}
                            studyRef={localState?.selectedStudy?.id}
                        />
                    ) : (
                        <LegacyPreviewTable table={{ ...currentTable, columns, study }} />
                    )}
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default EditColumns;
